import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import { api, apiErr } from "./api";
import { Field, PasswordInput, Message } from "./Login";
import LoginScene from "./login/LoginScene";
import LoginBrand from "./login/LoginBrand";
import "./login/login.css";

// Token travels in the URL fragment, so it never reaches server logs.
function readToken() {
  const t = new URLSearchParams(window.location.hash.slice(1)).get("t") || "";
  if (t) window.history.replaceState(null, "", window.location.pathname);
  return t;
}

function useInvite() {
  const [token] = useState(readToken);
  const [info, setInfo] = useState(undefined);
  const [loadErr, setLoadErr] = useState("");
  useEffect(() => {
    if (!token) { setInfo(null); setLoadErr("Link de convite ausente ou incompleto."); return; }
    api.post("/auth/invite-info", { token }).then(({ data }) => setInfo(data))
      .catch((e) => { setInfo(null); setLoadErr(apiErr(e)); });
  }, [token]);
  return { token, info, loadErr };
}

function InviteForm({ token, info }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (ev) => {
    ev.preventDefault(); setFormError("");
    const e = {};
    if (f.password.length < 10) e.password = "Use pelo menos 10 caracteres.";
    else if (f.password !== f.confirm) e.confirm = "As senhas não coincidem.";
    setErrors(e);
    if (Object.keys(e).length || loading) return;
    setLoading(true);
    try {
      await api.post("/auth/accept-invite", { token, password: f.password });
      setDone(true);
      setTimeout(() => navigate("/admin/login", { replace: true }), 2200);
    } catch (err) { setFormError(apiErr(err)); } finally { setLoading(false); }
  };

  if (done) return <Message kind="ok" testid="invite-success">Senha definida. Redirecionando para o login…</Message>;
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="invite-form">
      <Field id="invite-password" label="Nova senha" icon={Lock} error={errors.password}>
        <PasswordInput id="invite-password" value={f.password} onChange={set("password")} autoComplete="new-password" invalid={!!errors.password} testid="invite-password" />
      </Field>
      <Field id="invite-confirm" label="Confirmar senha" icon={Lock} error={errors.confirm}>
        <PasswordInput id="invite-confirm" value={f.confirm} onChange={set("confirm")} autoComplete="new-password" invalid={!!errors.confirm} testid="invite-confirm" />
      </Field>
      {formError && <Message kind="err" testid="invite-error">{formError}</Message>}
      <input type="hidden" autoComplete="username" value={info.email} readOnly />
      <button type="submit" data-testid="invite-submit" disabled={loading} className="lg-btn">
        {loading ? <><span className="lg-spin" aria-hidden="true" /> Salvando…</> : <>Ativar acesso <ArrowRight size={17} className="lg-btn-arrow" aria-hidden="true" /></>}
      </button>
    </form>
  );
}

export default function Invite() {
  const reduce = useReducedMotion();
  const { token, info, loadErr } = useInvite();
  return (
    <div className="mia-admin lg-root" data-testid="invite-page">
      <LoginScene />
      <header className="lg-top">
        <span />
        <Link to="/admin/login" className="lg-back" data-testid="invite-back-login"><ArrowLeft size={12} /> Ir para o login</Link>
      </header>
      <main className="lg-layout">
        <LoginBrand />
        <section className="lg-dock" aria-label="Ativar convite">
          <span className="lg-beam" aria-hidden="true" />
          <motion.div className="lg-form-wrap" initial={{ opacity: 0, x: reduce ? 0 : 28 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}>
            <span className="lg-tag">Convite de acesso</span>
            <h1 className="lg-title" data-testid="invite-title">{info ? `Olá, ${info.name.split(" ")[0]}` : "Ativar acesso"}</h1>
            {info === undefined && <p className="lg-desc">Validando convite…</p>}
            {info === null && <Message kind="err" testid="invite-invalid">{loadErr}</Message>}
            {info && (
              <>
                <p className="lg-desc" data-testid="invite-desc">
                  Defina sua senha para entrar como <strong>{info.role_label}</strong> com <span className="mono">{info.email}</span>.
                  Use pelo menos 10 caracteres, com letras e números ou símbolos.
                </p>
                <InviteForm token={token} info={info} />
              </>
            )}
          </motion.div>
        </section>
      </main>
    </div>
  );
}
